// LevelUpModal — guided level-up flow. Shows what the next level grants, then
// collects HP method, ASI/feat allocation, subclass pick, and new spells, and
// commits via the API. Supports leveling the existing class(es) or multiclassing.
import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import {
  ABILITY_KEYS,
  type AbilityKey,
  type FullCharacter,
  type LevelUpOptions,
  type SrdMeta,
} from "../lib/types";
import { ABILITY_ABBR, signed, titleizeSlug } from "../lib/format";
import { Modal, Spinner } from "./ui";
import { useToast } from "../hooks/useToast";
import { cantripsKnown, multiclassPrereqOk, spellsKnown } from "./srdBudgets";

export function LevelUpModal({
  gameId,
  character,
  meta,
  onClose,
  onLeveled,
}: {
  gameId: string;
  character: FullCharacter;
  meta: SrdMeta;
  onClose: () => void;
  onLeveled: (ch: FullCharacter) => void;
}) {
  const toast = useToast();
  // Which class to level — existing classes first, then any class for multiclass.
  const existingClasses = character.classes.map((c) => c.class);
  const [classIndex, setClassIndex] = useState(existingClasses[0] || meta.classes[0].index);
  const [opts, setOpts] = useState<LevelUpOptions | null>(null);
  const [loading, setLoading] = useState(false);
  const [committing, setCommitting] = useState(false);

  const [hpMethod, setHpMethod] = useState<"average" | "roll" | "max">("average");
  const [subclass, setSubclass] = useState<string | null>(null);
  const [asi, setAsi] = useState<Record<AbilityKey, number>>({ str: 0, dex: 0, con: 0, int: 0, wis: 0, cha: 0 });
  const [newCantrips, setNewCantrips] = useState<string[]>([]);
  const [newSpells, setNewSpells] = useState<string[]>([]);
  const [classSpellList, setClassSpellList] = useState<Record<string, { index: string; name: string }[]>>({});

  useEffect(() => {
    setLoading(true);
    setAsi({ str: 0, dex: 0, con: 0, int: 0, wis: 0, cha: 0 });
    setNewCantrips([]); setNewSpells([]); setSubclass(null);
    api.levelUpOptions(gameId, character.id, classIndex)
      .then((o) => {
        setOpts(o);
        if (o.subclass_options.length) setSubclass(o.subclass_options[0].index);
      })
      .catch((e) => toast.push(e instanceof Error ? e.message : "Failed to load options", "error"))
      .finally(() => setLoading(false));
    // load class spell list for casters
    api.classSpells(classIndex).then((r) => setClassSpellList(r.by_level)).catch(() => setClassSpellList({}));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classIndex]);

  const asiTotal = useMemo(() => ABILITY_KEYS.reduce((s, a) => s + asi[a], 0), [asi]);
  const asiValid = !opts?.grants_asi || asiTotal === 2;

  function bumpAsi(ab: AbilityKey, delta: number) {
    setAsi((prev) => {
      const cur = prev[ab];
      const next = cur + delta;
      if (next < 0 || next > 2) return prev;
      if (delta > 0 && asiTotal >= 2) return prev;
      // cap final score at 20
      if (delta > 0 && character.scores[ab] + next > 20) return prev;
      return { ...prev, [ab]: next };
    });
  }

  async function commit() {
    if (!opts) return;
    if (!asiValid) { toast.push("Allocate exactly 2 ability points", "error"); return; }
    if (opts.needs_subclass && !subclass) { toast.push("Choose a subclass", "error"); return; }
    if (opts.is_new_class && !multiclassPrereqOk(character.scores, existingClasses, classIndex)) {
      toast.push("You don't meet the ability prerequisites to multiclass into this class.", "error");
      return;
    }
    setCommitting(true);
    try {
      const asiPayload = opts.grants_asi
        ? Object.fromEntries(ABILITY_KEYS.filter((a) => asi[a] > 0).map((a) => [a, asi[a]]))
        : null;
      const res = await api.levelUp(gameId, character.id, {
        class_index: classIndex,
        subclass_index: opts.needs_subclass ? subclass : null,
        hp_method: hpMethod,
        asi: asiPayload,
        new_cantrips: newCantrips,
        new_spells: newSpells,
      });
      const s = res.summary;
      toast.push(`${s.name} reached level ${s.level_after}! +${s.hp_gained} HP`, "success");
      onLeveled(res.character);
      onClose();
    } catch (e) {
      toast.push(e instanceof Error ? e.message : "Level-up failed", "error");
    } finally {
      setCommitting(false);
    }
  }

  const cantripList = classSpellList["0"] || [];
  // Offer spell picks up to the highest currently-known/unlocked level.
  const learnableLevels = opts?.new_spell_levels?.length
    ? opts.new_spell_levels
    : Object.keys(character.spell_slots).map(Number);

  // How many NEW cantrips / spells this single level grants (the delta between
  // the known-count at the new class level and the old one). null → the class
  // has no fixed budget (prepared casters); the backend leaves those uncapped.
  const knownBefore = (fn: (c: string, l: number) => number | null) =>
    opts && opts.from_level >= 1 ? (fn(classIndex, opts.from_level) ?? 0) : 0;
  const cantripsLearnable = opts && cantripsKnown(classIndex, opts.to_level) != null
    ? Math.max(0, (cantripsKnown(classIndex, opts.to_level) ?? 0) - knownBefore(cantripsKnown))
    : null;
  const spellsLearnable = opts && spellsKnown(classIndex, opts.to_level) != null
    ? Math.max(0, (spellsKnown(classIndex, opts.to_level) ?? 0) - knownBefore(spellsKnown))
    : null;
  const cantripsAtCap = cantripsLearnable != null && newCantrips.length >= cantripsLearnable;
  const spellsAtCap = spellsLearnable != null && newSpells.length >= spellsLearnable;

  function toggleLimited(list: string[], set: (v: string[]) => void, idx: string, cap: number | null) {
    if (list.includes(idx)) set(list.filter((x) => x !== idx));
    else if (cap == null || list.length < cap) set([...list, idx]);
  }

  return (
    <Modal onClose={onClose}>
      <h2>Level Up — {character.name}</h2>
      <div className="modal__sub">
        Currently {character.classes.map((c) => `${titleizeSlug(c.class)} ${c.level}`).join(" / ")} ·
        Total level {character.level}
      </div>

      <div className="form-row">
        <label className="label">Advance a class</label>
        <select className="field" value={classIndex} onChange={(e) => setClassIndex(e.target.value)}>
          <optgroup label="Your classes">
            {existingClasses.map((ci) => (
              <option key={ci} value={ci}>{titleizeSlug(ci)} (continue)</option>
            ))}
          </optgroup>
          <optgroup label="Multiclass into">
            {meta.classes.filter((c) => !existingClasses.includes(c.index)).map((c) => {
              // SRD: you need 13 in the key ability of both your current class(es)
              // and the one you're multiclassing into.
              const ok = multiclassPrereqOk(character.scores, existingClasses, c.index);
              return (
                <option key={c.index} value={c.index} disabled={!ok}>
                  {c.name}{c.spellcasting ? " ✦" : ""}{ok ? "" : " — needs 13 in its key ability"}
                </option>
              );
            })}
          </optgroup>
        </select>
        {!existingClasses.includes(classIndex) && !multiclassPrereqOk(character.scores, existingClasses, classIndex) && (
          <div className="hint" style={{ color: "var(--danger, #c0392b)" }}>
            You don't meet the ability prerequisites to multiclass into {titleizeSlug(classIndex)} (13+ in the key
            ability of both classes).
          </div>
        )}
      </div>

      {loading || !opts ? (
        <div className="center-load"><Spinner /> Loading level options…</div>
      ) : (
        <>
          <div className="levelup-banner card">
            <div className="lu-arrow">
              <span className="lu-from">{opts.is_new_class ? "—" : opts.from_level}</span>
              <span className="lu-to-arrow">→</span>
              <span className="lu-to">{opts.class_name} {opts.to_level}</span>
            </div>
            <div className="muted" style={{ fontSize: 13 }}>Total level {opts.total_level_after}</div>
          </div>

          {opts.features.length > 0 && (
            <div className="form-row">
              <label className="label">New Features</label>
              <div className="tag-row">
                {opts.features.map((f) => <span key={f} className="chip chip--gold">{f}</span>)}
              </div>
            </div>
          )}

          {/* HP */}
          <div className="form-row">
            <label className="label">Hit Points (d{opts.hit_die} + {signed(opts.con_mod)} CON)</label>
            <div className="seg">
              <button className={hpMethod === "average" ? "on" : ""} onClick={() => setHpMethod("average")}>
                Average (+{opts.average_hp + opts.con_mod})
              </button>
              <button className={hpMethod === "roll" ? "on" : ""} onClick={() => setHpMethod("roll")}>Roll</button>
              <button className={hpMethod === "max" ? "on" : ""} onClick={() => setHpMethod("max")}>
                Max (+{opts.hit_die + opts.con_mod})
              </button>
            </div>
          </div>

          {/* Subclass */}
          {opts.needs_subclass && (
            <div className="form-row">
              <label className="label">Choose your subclass</label>
              <select className="field" value={subclass ?? ""} onChange={(e) => setSubclass(e.target.value)}>
                {opts.subclass_options.map((s) => <option key={s.index} value={s.index}>{s.name}</option>)}
              </select>
            </div>
          )}

          {/* ASI */}
          {opts.grants_asi && (
            <div className="form-row">
              <label className="label">
                Ability Score Improvement — allocate 2 points
                <span className={`pick-count ${asiValid ? "" : "over"}`} style={{ marginLeft: 8 }}>{asiTotal}/2</span>
              </label>
              <div className="score-grid">
                {ABILITY_KEYS.map((ab) => {
                  const final = character.scores[ab] + asi[ab];
                  return (
                    <div className="score-box" key={ab}>
                      <div className="ab">{ABILITY_ABBR[ab]}</div>
                      <div className="val">{final}{asi[ab] > 0 && <span style={{ color: "var(--jade)", fontSize: 14 }}> +{asi[ab]}</span>}</div>
                      <div className="steppers">
                        <button disabled={asi[ab] <= 0} onClick={() => bumpAsi(ab, -1)}>−</button>
                        <button disabled={asiTotal >= 2 || final >= 20} onClick={() => bumpAsi(ab, 1)}>+</button>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="hint">Max 20 per ability. (Feats are a future option; ASI for now.)</div>
            </div>
          )}

          {/* New spells for casters. Caps mirror the backend's per-level budget:
              you may only learn as many as this level actually grants. */}
          {opts.is_caster && cantripList.length > 0 && cantripsLearnable !== 0 && (
            <div className="form-row">
              <label className="label">
                Learn a Cantrip (optional)
                {cantripsLearnable != null && (
                  <span className={`pick-count${cantripsAtCap ? " over" : ""}`} style={{ marginLeft: 8 }}>
                    {newCantrips.length}/{cantripsLearnable}
                  </span>
                )}
              </label>
              <div className="pick-grid">
                {cantripList.filter((s) => !character.spells.some((sp) => sp.index === s.index)).map((s) => {
                  const on = newCantrips.includes(s.index);
                  const atCap = cantripsAtCap && !on;
                  return (
                    <button key={s.index} className={`pick${on ? " pick--on" : ""}${atCap ? " pick--disabled" : ""}`}
                      disabled={atCap}
                      onClick={() => toggleLimited(newCantrips, setNewCantrips, s.index, cantripsLearnable)}>
                      {s.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {opts.is_caster && learnableLevels.length > 0 && spellsLearnable !== 0 && (
            <div className="form-row">
              <label className="label">
                Learn Spells (optional)
                {spellsLearnable != null && (
                  <span className={`pick-count${spellsAtCap ? " over" : ""}`} style={{ marginLeft: 8 }}>
                    {newSpells.length}/{spellsLearnable}
                  </span>
                )}
              </label>
              <div className="spell-levels">
                {learnableLevels.map((lvl) => {
                  const list = (classSpellList[String(lvl)] || [])
                    .filter((s) => !character.spells.some((sp) => sp.index === s.index));
                  if (!list.length) return null;
                  return (
                    <div className="spell-level-group" key={lvl}>
                      <div className="slg-head">Level {lvl}</div>
                      <div className="pick-grid">
                        {list.map((s) => {
                          const on = newSpells.includes(s.index);
                          const atCap = spellsAtCap && !on;
                          return (
                            <button key={s.index} className={`pick${on ? " pick--on" : ""}${atCap ? " pick--disabled" : ""}`}
                              disabled={atCap}
                              onClick={() => toggleLimited(newSpells, setNewSpells, s.index, spellsLearnable)}>
                              {s.name}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="modal__row">
            <button className="btn btn--ghost" onClick={onClose}>Cancel</button>
            <button className="btn btn--primary" disabled={committing || !asiValid} onClick={commit}>
              {committing ? <Spinner /> : `Confirm — Level ${opts.total_level_after}`}
            </button>
          </div>
        </>
      )}
    </Modal>
  );
}
